import React, { useRef, useState, useEffect } from "react";
import {
  Animated,
  TouchableOpacity,
  Text,
  StyleSheet,
  Platform,
  View,
  Easing,
  type TouchableOpacityProps,
  type GestureResponderEvent,
  type StyleProp,
  type TextStyle,
} from "react-native";
import { useThemeColor } from "@/hooks/useThemeColor";
import { useComponentTheme } from "@/hooks/useComponentTheme";
import { ButtonPaddings } from "@/constants/themes/components";
import { MaterialShapes, MaterialElevations, Fonts } from "@/constants/ThemeColours";
import { trackButtonClick } from "@/services/analytics";
import { Ionicons } from "@expo/vector-icons";

const AnimatedTouchable = Animated.createAnimatedComponent(TouchableOpacity);

/**
 * Interface for ThemedButton props adhering to platform standards.
 */
export interface IThemedButtonProps extends Omit<TouchableOpacityProps, "onPress"> {
  title: string;
  icon?: React.ReactNode;
  iconPosition?: "left" | "right";
  textStyle?: StyleProp<TextStyle>;
  variant?: "primary" | "secondary" | "danger" | "minimal";
  /** Button density sizing: 'sm' | 'compact' | 'md' | 'lg' (default: 'md' or 'compact' if compact=true) */
  size?: "sm" | "compact" | "md" | "lg";
  /** Convenience flag matching size="compact" */
  compact?: boolean;
  lightColor?: string;
  darkColor?: string;
  hoverBorderColor?: string;
  loading?: boolean;
  onPress?: (event: GestureResponderEvent) => any;
}

export type ThemedButtonProps = IThemedButtonProps;

/**
 * Standard themed button following platform compact style guide with crisp hover border
 * and built-in indeterminate linear progress bar for pending actions.
 *
 * @param {IThemedButtonProps} props - Button properties.
 * @returns {React.JSX.Element} The rendered button.
 */
export function ThemedButton({
  title,
  icon,
  iconPosition = "left",
  textStyle,
  variant = "primary",
  size,
  compact = false,
  style,
  lightColor,
  darkColor,
  hoverBorderColor,
  disabled,
  loading = false,
  onPress,
  onPressIn,
  onPressOut,
  ...rest
}: IThemedButtonProps): React.JSX.Element {
  const { button: buttonTheme } = useComponentTheme();
  const effectiveSize = size || (compact ? "compact" : "md");
  const sizeTokens = buttonTheme.sizes[effectiveSize] || buttonTheme.sizes.md;
  const variantTokens = buttonTheme.variants[variant] || buttonTheme.variants.primary;

  const [isHovered, setIsHovered] = useState(false);
  const [isActionPending, setIsActionPending] = useState(false);
  const scaleAnim = useRef(new Animated.Value(1)).current;
  const progressAnim = useRef(new Animated.Value(0)).current;

  const isBusy = Boolean(loading || isActionPending);

  useEffect(() => {
    let animLoop: Animated.CompositeAnimation | null = null;
    if (isBusy) {
      animLoop = Animated.loop(
        Animated.timing(progressAnim, {
          toValue: 1,
          duration: 1200,
          easing: Easing.linear,
          useNativeDriver: false,
        })
      );
      animLoop.start();
    } else {
      progressAnim.setValue(0);
    }
    return () => {
      if (animLoop) {
        animLoop.stop();
      }
    };
  }, [isBusy, progressAnim]);

  const primaryTextColor = useThemeColor({ light: lightColor, dark: darkColor }, "buttonText");
  const secondaryTextColor = useThemeColor({ light: lightColor, dark: darkColor }, "buttonSecondaryText");
  const dangerTextColor = useThemeColor({ light: lightColor, dark: darkColor }, "buttonDangerText");
  const defaultTextColor = useThemeColor({ light: lightColor, dark: darkColor }, "text");

  const textColor =
    variant === "primary"
      ? primaryTextColor
      : variant === "secondary"
      ? secondaryTextColor
      : variant === "danger"
      ? dangerTextColor
      : defaultTextColor;

  const buttonBgColor = useThemeColor(
    { light: lightColor, dark: darkColor },
    variant === "primary"
      ? "button"
      : variant === "danger"
      ? "danger"
      : variant === "secondary"
      ? "buttonSecondary"
      : "transparent",
  );

  const standardBorder = useThemeColor({ light: lightColor, dark: darkColor }, "border");
  const secondaryBorder = useThemeColor({ light: lightColor, dark: darkColor }, "buttonSecondaryBorder");

  // Hover border tokens
  const buttonHoverBorder = useThemeColor({ light: hoverBorderColor, dark: hoverBorderColor }, "buttonHoverBorder");
  const buttonSecondaryHoverBorder = useThemeColor({}, "buttonSecondaryHoverBorder");
  const buttonDangerHoverBorder = useThemeColor({}, "buttonDangerHoverBorder");
  const textHoverBorder = useThemeColor({}, "text");

  const activeHoverBorderColor =
    hoverBorderColor ||
    (variant === "secondary"
      ? buttonSecondaryHoverBorder
      : variant === "danger"
      ? buttonDangerHoverBorder
      : variant === "minimal"
      ? textHoverBorder
      : buttonHoverBorder);

  // Normal resting border
  const restingBorderColor =
    variant === "secondary"
      ? secondaryBorder
      : variant === "minimal"
      ? standardBorder
      : "transparent";

  const currentBorderColor = isHovered && !disabled && !isBusy ? activeHoverBorderColor : restingBorderColor;

  const handleMouseEnter = (): void => {
    if (!disabled && !isBusy) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = (): void => {
    setIsHovered(false);
  };

  const handlePressIn = (e: GestureResponderEvent): void => {
    if (disabled || isBusy) return;
    Animated.timing(scaleAnim, {
      toValue: 0.98,
      duration: 100,
      useNativeDriver: Platform.OS !== "web",
    }).start();
    onPressIn?.(e);
  };

  const handlePressOut = (e: GestureResponderEvent): void => {
    if (disabled || isBusy) return;
    Animated.spring(scaleAnim, {
      toValue: 1,
      friction: 8,
      tension: 40,
      useNativeDriver: Platform.OS !== "web",
    }).start();
    onPressOut?.(e);
  };

  const handlePress = async (e: GestureResponderEvent): Promise<void> => {
    if (disabled || isBusy) {
      return;
    }
    trackButtonClick(title, undefined, { variant });
    if (!onPress) {
      return;
    }
    try {
      const result: unknown = onPress(e);
      if (result && typeof (result as Promise<unknown>).then === "function") {
        setIsActionPending(true);
        try {
          await result;
        } finally {
          setIsActionPending(false);
        }
      }
    } catch (err) {
      setIsActionPending(false);
      throw err;
    }
  };

  const progressTrackColor =
    variant === "primary" || variant === "danger"
      ? "rgba(255, 255, 255, 0.25)"
      : "rgba(74, 96, 143, 0.2)";

  const progressFillColor =
    variant === "primary" || variant === "danger"
      ? "#FFFFFF"
      : variant === "secondary"
      ? secondaryTextColor
      : primaryTextColor;

  const progressLeft = progressAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ["-40%", "100%"],
  });

  const renderedIcon =
    typeof icon === "string" ? (
      <Ionicons name={icon as any} size={15} color={textColor} />
    ) : (
      icon
    );

  return (
    <AnimatedTouchable
      activeOpacity={0.85}
      disabled={disabled || isBusy}
      onPress={handlePress}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      onHoverIn={handleMouseEnter}
      onHoverOut={handleMouseLeave}
      {...Platform.select({
        web: {
          onMouseEnter: handleMouseEnter,
          onMouseLeave: handleMouseLeave,
          onPointerEnter: handleMouseEnter,
          onPointerLeave: handleMouseLeave,
        } as any,
        default: {},
      })}
      style={[
        styles.base,
        {
          paddingVertical: sizeTokens.paddingVertical,
          paddingHorizontal: sizeTokens.paddingHorizontal,
          minHeight: sizeTokens.minHeight,
          borderRadius: buttonTheme.borderRadius,
          borderWidth: buttonTheme.borderWidth,
          backgroundColor: buttonBgColor,
          borderColor: currentBorderColor,
          transform: [{ scale: scaleAnim }],
        },
        (disabled || isBusy) && styles.disabled,
        Platform.OS === "web" &&
          ({
            cursor: disabled || isBusy ? "not-allowed" : "pointer",
            transition: "border-color 0.15s ease, background-color 0.15s ease",
          } as any),
        style,
      ]}
      {...rest}
    >
      {renderedIcon ? (
        <View style={[styles.contentRow, { gap: sizeTokens.gap }]}>
          {iconPosition !== "right" && <View style={styles.iconContainer}>{renderedIcon}</View>}
          {title ? (
            <Text style={[styles.text, { fontSize: sizeTokens.fontSize, color: textColor }, isBusy && styles.textBusy, textStyle]}>
              {title}
            </Text>
          ) : null}
          {iconPosition === "right" && <View style={styles.iconContainer}>{renderedIcon}</View>}
        </View>
      ) : (
        <Text style={[styles.text, { fontSize: sizeTokens.fontSize, color: textColor }, isBusy && styles.textBusy, textStyle]}>
          {title}
        </Text>
      )}
      {isBusy && (
        <View style={[styles.progressBarTrack, { height: buttonTheme.progressBarHeight, backgroundColor: progressTrackColor }]}>
          <Animated.View
            style={[
              styles.progressBarFill,
              {
                backgroundColor: progressFillColor,
                left: progressLeft,
              },
            ]}
          />
        </View>
      )}
    </AnimatedTouchable>
  );
}

const styles = StyleSheet.create({
  base: {
    paddingVertical: ButtonPaddings.standard.paddingVertical,
    paddingHorizontal: ButtonPaddings.standard.paddingHorizontal,
    borderRadius: Platform.select({ android: MaterialShapes.small, default: 4 }),
    borderWidth: 2,
    borderColor: "transparent",
    alignItems: "center",
    justifyContent: "center",
    marginVertical: 2,
    ...Platform.select({
      android: MaterialElevations.level1,
      default: {
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.03,
        shadowRadius: 2,
        elevation: 1,
      },
    }),
    overflow: "hidden",
    position: "relative",
  },
  contentRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
  },
  iconContainer: {
    alignItems: "center",
    justifyContent: "center",
  },
  disabled: {
    opacity: 0.5,
  },
  text: {
    fontFamily: Fonts.sans,
    fontSize: 13,
    fontWeight: "600",
    letterSpacing: 0.15,
  },
  textBusy: {
    opacity: 0.8,
  },
  progressBarTrack: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: 3,
    overflow: "hidden",
  },
  progressBarFill: {
    position: "absolute",
    top: 0,
    bottom: 0,
    width: "40%",
    borderRadius: 1.5,
  },
});

export default ThemedButton;

